import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faBuilding,
  faBan,
  faCircleCheck,
  faRotateLeft,
  faXmark,
  faEnvelope,
  faPaperPlane,
  faTriangleExclamation,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { platformCompanyService } from '@/services/platform-company.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import type { PlatformTenantDetail } from '@/types/platform-auth.types';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import {
  CompanyStatusBadge,
  formatCompanyDate,
  formatCompanyDateTime,
} from './company-presentation';
import { CompanyLifecycleDialog, type LifecycleAction } from './CompanyLifecycleDialog';

/**
 * Phase 12.3G — Company Detail.
 *
 * Design of record: `COMPANIES_DESIGN.md` v1.0 §5.2 and §6. Stacked cards, no
 * tabs (the console has no tab precedent). Statistics are deliberately absent —
 * there is no endpoint and no established product reason, and §5.2 says so
 * explicitly. Nothing is invented to fill a card; a field with no value renders
 * an em dash.
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A route param is always a string once the route matches, so a caller that
 * interpolated a missing id lands here with the literal text "undefined" rather
 * than an absent value. That is a broken link, not a missing record — it is
 * bounced back to the list instead of being reported as a 404, and it never
 * reaches the API (which would answer 400 from ParseUUIDPipe).
 */
function isRoutableId(value: string | undefined): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** Which transitions are legal from here — the same machine the backend enforces (§6.1). */
function allowedActions(status: PlatformTenantDetail['status']): LifecycleAction[] {
  switch (status) {
    case 'TRIAL':
      return ['activate', 'cancel'];
    case 'ACTIVE':
      return ['suspend', 'cancel'];
    case 'SUSPENDED':
      return ['reactivate', 'cancel'];
    case 'CANCELLED':
      // Terminal (D-5). No action, and no disabled buttons pretending otherwise.
      return [];
    default:
      return [];
  }
}

const ACTION_ICON: Record<LifecycleAction, typeof faBan> = {
  activate: faCircleCheck,
  suspend: faBan,
  reactivate: faRotateLeft,
  cancel: faXmark,
};

const ACTION_LABEL: Record<LifecycleAction, string> = {
  activate: 'Activate',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  cancel: 'Cancel company',
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h2 className="mb-4 text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm text-foreground">{value ?? '—'}</dd>
    </div>
  );
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = usePlatformAuthStore((s) => s.user?.role);
  const canManageLifecycle = canPlatform(role, 'platform:companies:suspend');
  const canCreate = canPlatform(role, 'platform:companies:create');

  const [company, setCompany] = useState<PlatformTenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [action, setAction] = useState<LifecycleAction | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const [resending, setResending] = useState(false);
  const [inviteNotice, setInviteNotice] = useState('');
  const [inviteError, setInviteError] = useState('');

  const routable = isRoutableId(id);

  const load = useCallback(() => {
    if (!routable) return;
    setLoading(true);
    platformCompanyService
      .getById(id)
      .then((data) => {
        setCompany(data);
        setError('');
      })
      .catch(() => setError('Company not found or access denied.'))
      .finally(() => setLoading(false));
  }, [id, routable]);

  useEffect(() => {
    if (!routable) {
      // `replace` so the malformed URL does not stay in history and cannot be
      // reached again with Back.
      navigate(PLATFORM_ROUTES.COMPANIES, { replace: true });
      return;
    }
    load();
  }, [routable, navigate, load]);

  const runAction = async (reason: string) => {
    if (!company || !action) return;
    setActionSubmitting(true);
    setActionError('');
    try {
      const service = platformCompanyService;
      if (action === 'activate') await service.activate(company.tenantId, reason || undefined);
      if (action === 'suspend') await service.suspend(company.tenantId, reason);
      if (action === 'reactivate') await service.reactivate(company.tenantId, reason || undefined);
      if (action === 'cancel') await service.cancel(company.tenantId, reason);

      setAction(null);
      setNotice(`Company ${ACTION_LABEL[action].toLowerCase()} completed.`);
      // Re-read rather than patch local state: status, reason and timestamp are
      // all written server-side, and guessing them here is how a console starts
      // showing something the database does not agree with.
      load();
    } catch (err) {
      setActionError(
        err instanceof Error && err.message
          ? err.message
          : 'The action could not be completed. Nothing was changed.',
      );
    } finally {
      setActionSubmitting(false);
    }
  };

  const resendInvitation = async () => {
    if (!company) return;
    setResending(true);
    setInviteError('');
    setInviteNotice('');
    try {
      const result = await platformCompanyService.resendOwnerInvitation(company.tenantId);
      if (result.delivered) {
        setInviteNotice('A new invitation has been sent. Any earlier invitation is now invalid.');
      } else {
        setInviteError(
          'A new invitation was created but could not be emailed. You can try sending it again.',
        );
      }
    } catch (err) {
      setInviteError(
        err instanceof Error && err.message
          ? err.message
          : 'The invitation could not be sent. Please try again.',
      );
    } finally {
      setResending(false);
    }
  };

  // Without this the early return above would leave `loading` true forever and
  // the page would spin instead of redirecting.
  if (!routable) return null;

  if (loading && !company) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading company">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className="h-16 animate-pulse rounded-xl bg-muted" />
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-40 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p role="alert" className="text-sm text-destructive">
          {error || 'Company not found.'}
        </p>
        <button
          onClick={() => navigate(PLATFORM_ROUTES.COMPANIES)}
          className="text-sm text-primary hover:underline"
        >
          Back to Companies
        </button>
      </div>
    );
  }

  const actions = allowedActions(company.status);
  const owner = company.owner;

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(PLATFORM_ROUTES.COMPANIES)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        Back to Companies
      </button>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-muted">
            <FontAwesomeIcon icon={faBuilding} className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h1 className="break-words text-xl font-semibold text-foreground">{company.name}</h1>
            <p className="truncate text-sm text-muted-foreground">{company.slug}</p>
          </div>
        </div>
        <CompanyStatusBadge status={company.status} size="md" />
      </div>

      {notice && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-foreground"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-primary" />
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ── Overview ─────────────────────────────────────────────────────── */}
        <Card title="Overview">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Status" value={<CompanyStatusBadge status={company.status} />} />
            <Field label="Created" value={formatCompanyDateTime(company.createdAt)} />
            <Field label="Last updated" value={formatCompanyDateTime(company.updatedAt)} />
            <Field
              label="Tenant ID"
              value={<code className="break-all text-xs">{company.tenantId}</code>}
            />
            {company.statusReason && <Field label="Status reason" value={company.statusReason} />}
            {company.statusChangedAt && (
              <Field
                label="Status changed"
                value={formatCompanyDateTime(company.statusChangedAt)}
              />
            )}
          </dl>
        </Card>

        {/* ── Subscription ─────────────────────────────────────────────────── */}
        <Card title="Subscription">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Plan" value={company.subscription?.planName ?? '—'} />
            <Field label="Plan code" value={company.subscription?.planSlug ?? '—'} />
            <Field label="Subscription status" value={company.subscription?.status ?? '—'} />
            <Field
              label="Started"
              value={formatCompanyDate(company.subscription?.startedAt ?? null)}
            />
            <Field
              label="Expires"
              value={formatCompanyDate(company.subscription?.expiresAt ?? null)}
            />
          </dl>
        </Card>

        {/* ── Legal identity (G-5) ─────────────────────────────────────────── */}
        <Card title="Legal identity">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Company name" value={company.registration?.companyName ?? company.name} />
            <Field label="Registered name" value={company.registration?.registeredName ?? '—'} />
            <Field
              label="Registration number"
              value={company.registration?.registrationNumber ?? '—'}
            />
          </dl>
          {/*
            COMPANIES_DESIGN v1.1 — display only. Registered name and
            registration number are TENANT-owned: the company declares its own
            legal entity in Company Settings, and no platform permission grants
            the write. The previous wording ("can only be set from here") and the
            Edit control were removed with the ownership inversion.
          */}
          <p className="mt-4 text-xs text-muted-foreground">
            Registered name and registration number are maintained by the company in its own Company
            Settings. Company name is administered here.
          </p>
        </Card>

        {/* ── Owner (G-7) + invitation state (G-6) ─────────────────────────── */}
        <Card title="Owner">
          {owner ? (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Name" value={`${owner.firstName} ${owner.lastName}`.trim() || '—'} />
                <Field label="Email" value={owner.email ?? '—'} />
              </dl>

              {owner.onboardingPending ? (
                <div className="mt-4 space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        Waiting for the owner to set their password
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        An invitation was emailed to the owner. Nobody — including you — has set a
                        password for this account, and it cannot be signed into until the owner
                        completes onboarding from that email.
                      </p>
                    </div>
                  </div>

                  {canCreate && (
                    <button
                      type="button"
                      onClick={resendInvitation}
                      disabled={resending}
                      className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
                    >
                      <FontAwesomeIcon
                        icon={resending ? faSpinner : faPaperPlane}
                        className="h-3.5 w-3.5"
                        spin={resending}
                      />
                      {resending ? 'Sending…' : 'Resend invitation'}
                    </button>
                  )}

                  {inviteNotice && (
                    <p role="status" className="text-xs text-foreground">
                      {inviteNotice}
                    </p>
                  )}
                  {inviteError && (
                    <p role="alert" className="text-xs text-destructive">
                      {inviteError}
                    </p>
                  )}
                </div>
              ) : (
                <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                  <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-green-600" />
                  The owner has completed onboarding and set their own password.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              This company has no owner account on record.
            </p>
          )}
        </Card>
      </div>

      {/* ── Lifecycle (§6) ─────────────────────────────────────────────────── */}
      {canManageLifecycle && (
        <Card title="Lifecycle">
          {actions.length === 0 ? (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              This company is cancelled. Cancellation is permanent, so no further lifecycle action
              is available. All of its data has been retained.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {actions.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      setActionError('');
                      setNotice('');
                      setAction(a);
                    }}
                    className={
                      a === 'cancel' || a === 'suspend'
                        ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 sm:min-h-0'
                        : 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0'
                    }
                  >
                    <FontAwesomeIcon icon={ACTION_ICON[a]} className="h-3.5 w-3.5" />
                    {ACTION_LABEL[a]}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Suspending withholds access and retains all data. Cancelling is permanent. No
                company is ever deleted from this console.
              </p>
            </>
          )}
        </Card>
      )}

      {!canManageLifecycle && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Your role can view companies but not change their status.
        </p>
      )}

      <CompanyLifecycleDialog
        action={action}
        companyName={company.name}
        submitting={actionSubmitting}
        error={actionError}
        onClose={() => setAction(null)}
        onConfirm={(reason) => void runAction(reason)}
      />
    </div>
  );
}
